import type { TimestampInMillis, UUID } from '@cobliteam/common-types';
import type { GeofenceType } from '@cobliteam/common-types/src/geofenceV3';

import type { IncidentType } from '../costs';
import type { LatLng } from '../geocode';

export interface TrackingDriver {
  id: UUID;
  name?: string;
}

export interface TrackingPath {
  type: string;
  geometry: {
    type: string;
    coordinates: number[][];
  };
  properties: {
    timestamps: number[];
    speeds?: Array<number | null>;
  };
}

export enum TrackingSpeedEventSeverity {
  MEDIUM = 'medium',
  HIGH = 'high',
  HIGHER = 'higher',
}

export interface TrackingSpeedEvent {
  eventTime: number;
  driverName?: string;
  location: LatLng;
  speedInKmh: number;
  speedLimitInKmh: number;
  durationAboveInMillis?: number;
  severity: TrackingSpeedEventSeverity;
  startTime?: number;
  medianSpeedInKmh?: number;
  medianBasedSeverity?: TrackingSpeedEventSeverity;
  type: 'road' | 'vehicle';
}

export enum TrackingGeofenceEventTypeEnum {
  GEOFENCE_IN = 'geofence_in',
  GEOFENCE_OUT = 'geofence_out',
}

export interface TrackingGeofenceEvents {
  id: UUID;
  time: number;
  lng: number;
  lat: number;
  eventType: TrackingGeofenceEventTypeEnum;
  geofence: {
    id: UUID;
    name: string;
    category: GeofenceType;
  };
}

export interface TrackingGeofence {
  name: string;
  category?: string;
}

export interface TrackingStop {
  startTime: TimestampInMillis;
  endTime: TimestampInMillis;
  driverName?: string;
  location: LatLng;
  idleOnInMillis: number;
  idleOffInMillis: number;
  hasFilteredIdleOnTime: boolean;
  geofences: TrackingGeofence[];
}

export interface TrackingSummary {
  totalFuelCost: number;
  totalDistance: number;
  totalDuration: number;
  totalStops: number;
}

export interface FullTrackingSummary {
  totalFuelCost: number;
  totalDistance: number;
  totalDuration: number;
  totalStops: number;
  totalSpeedEvents: number;
}

export interface TrackingIncident {
  type: IncidentType;
  time: number;
  latitude?: number;
  longitude?: number;
}

export interface TrackingVehicle {
  id: UUID;
  licensePlate?: string;
  brand?: string;
  model?: string;
  fuelConsumption?: number;
  fuelPrice?: number;
}

export interface TrackingLastLocation {
  isPlugged?: boolean;
  pluggedUpdateTimestamp?: TimestampInMillis;
}

export interface TrackingDevice {
  type: string;
  cobliId: string;
  lastLocation: TrackingLastLocation;
}

export interface TrackingSpeedEvents {
  roadSpeedEvents: TrackingSpeedEvent[];
  vehicleSpeedEvents: TrackingSpeedEvent[];
  geofenceEvents: TrackingGeofenceEvents[];
  summary?: { totalSpeedEvents: number };
}

// to be removed
export interface FullTrackingGraphQL {
  vehicleId?: UUID;
  drivers: TrackingDriver[];
  pathGeojson: TrackingPath;
  roadSpeedEvents: TrackingSpeedEvent[];
  vehicleSpeedEvents: TrackingSpeedEvent[];
  stops: TrackingStop[];
  summary?: FullTrackingSummary;
  incidents: TrackingIncident[];
  vehicle?: TrackingVehicle;
  device?: TrackingDevice;
}

export interface TrackingGraphQL {
  vehicleId?: UUID;
  pathGeojson: TrackingPath;
  stops: TrackingStop[];
  summary?: TrackingSummary;
  incidents: TrackingIncident[];
  vehicle?: TrackingVehicle;
  device?: TrackingDevice;
}
